import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectValue } from 'antd/lib/select';

import './index.less';

import {
  AgentResourceELMA365AppSettingsTypeEnum,
  AgentResourceELMA365FileSettingsTypeEnum,
  AgentResourceELMA365SessionSettingsTypeEnum,
  AgentResourceModel,
  AgentResourceTextSettingsTypeEnum,
} from '../../../../gamma-api';
import IbTypography from '../../common/IbTypography';
import IbInput from '../../common/IbInput';
import IbButton from '../../common/IbButton';
import IbTextArea from '../../common/IbTextArea';
import IbSelect from '../../common/IbSelect';

import { EMPTY_AGENT_RESOURCE, resourceSettingsMap } from './const';
import {
  AgentResourceELMA365AppSettingsType,
  AgentResourceELMA365FileSettingsType,
  AgentResourceELMA365SessionSettingsType,
  AgentResourceSettingsType,
  AgentResourceTextSettingsType,
} from './types';
import { buildELMA365AppSettings, buildELMA365FileSettings, buildELMA365SessionSettings } from './utils';

const MAIN_CLASS_NAME = 'add-resource-form';
const FORM_ITEM_CLASS_NAME = `${MAIN_CLASS_NAME}__form-item`;
const FORM_FOOTER_CLASS_NAME = `${MAIN_CLASS_NAME}__form-footer`;

export interface IAddResourceFormProps {
  visible: boolean;
  onAdd: (resource: AgentResourceModel) => void;
}

const AddResourceForm: React.FC<IAddResourceFormProps> = ({ visible, onAdd }) => {
  const { t } = useTranslation();

  const [resource, setResource] = useState<AgentResourceModel>(EMPTY_AGENT_RESOURCE);

  const onNameChange = (value: string) => {
    setResource({ ...resource, name: value });
  };

  const onSettingsTypeChange = (value: SelectValue) => {
    setResource({ ...resource, settings: resourceSettingsMap[value as AgentResourceSettingsType] });
  };

  const onTextContentChange = (value: string) => {
    setResource({
      ...resource,
      settings: {
        ...(resource.settings as AgentResourceTextSettingsType),
        content: value,
      },
    });
  };

  const onELMA365AppViewUrlChange = (value: string) => {
    setResource({
      ...resource,
      settings: buildELMA365AppSettings(value),
    });
  };

  const onELMA365SessionViewUrlChange = (value: string) => {
    setResource({
      ...resource,
      settings: buildELMA365SessionSettings(value),
    });
  };

  const onELMA365FileViewUrlChange = (value: string) => {
    setResource({
      ...resource,
      settings: buildELMA365FileSettings(value),
    });
  };

  const onAddButtonClick = () => {
    onAdd(resource);
  };

  const onVisiblePropChange = () => {
    if (visible) {
      setResource({ ...EMPTY_AGENT_RESOURCE });
    }
  };
  useEffect(onVisiblePropChange, [visible]);

  const renderTextSettings = (settings: AgentResourceTextSettingsType) => (
    <div className={FORM_ITEM_CLASS_NAME}>
      <IbTypography.Paragraph type="secondary">
        {t('Agent: Testing chat: Add resource form: Content: Title')}
      </IbTypography.Paragraph>
      <IbTextArea rows={6} value={settings.content} onChange={onTextContentChange} />
    </div>
  );

  const renderParsedData = (data: unknown) => (
    <IbTypography.Paragraph type="descriptor">
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </IbTypography.Paragraph>
  );

  const renderELMA365AppSettings = (settings: AgentResourceELMA365AppSettingsType) => (
    <div className={FORM_ITEM_CLASS_NAME}>
      <IbTypography.Paragraph type="secondary">
        {t('Agent: Testing chat: Add resource form: ELMA365 app view URL: Title')}
      </IbTypography.Paragraph>
      <IbInput value={settings.view_url} onChange={onELMA365AppViewUrlChange} />
      {renderParsedData({
        base_url: settings.base_url,
        namespace: settings.namespace,
        code: settings.code,
        id: settings.id,
      })}
    </div>
  );

  const renderELMA365SessionSettings = (settings: AgentResourceELMA365SessionSettingsType) => (
    <div className={FORM_ITEM_CLASS_NAME}>
      <IbTypography.Paragraph type="secondary">
        {t('Agent: Testing chat: Add resource form: ELMA365 session view URL: Title')}
      </IbTypography.Paragraph>
      <IbInput value={settings.view_url} onChange={onELMA365SessionViewUrlChange} />
      {renderParsedData({ base_url: settings.base_url, session_id: settings.session_id })}
    </div>
  );

  const renderELMA365FileSettings = (settings: AgentResourceELMA365FileSettingsType) => (
    <div className={FORM_ITEM_CLASS_NAME}>
      <IbTypography.Paragraph type="secondary">
        {t('Agent: Testing chat: Add resource form: ELMA365 file view URL: Title')}
      </IbTypography.Paragraph>
      <IbInput value={settings.view_url} onChange={onELMA365FileViewUrlChange} />
      {renderParsedData({ base_url: settings.base_url, file_id: settings.file_id })}
    </div>
  );

  const renderSettings = () => {
    if (resource.settings.type == AgentResourceTextSettingsTypeEnum.Text) {
      return renderTextSettings(resource.settings);
    }
    if (resource.settings.type == AgentResourceELMA365AppSettingsTypeEnum.Elma365App) {
      return renderELMA365AppSettings(resource.settings);
    }
    if (resource.settings.type == AgentResourceELMA365SessionSettingsTypeEnum.Elma365Session) {
      return renderELMA365SessionSettings(resource.settings);
    }
    if (resource.settings.type == AgentResourceELMA365FileSettingsTypeEnum.Elma365File) {
      return renderELMA365FileSettings(resource.settings);
    }
    return null;
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Testing chat: Add resource form: Type: Title')}
        </IbTypography.Paragraph>
        <IbSelect
          options={[
            { value: AgentResourceTextSettingsTypeEnum.Text, label: t('Agent: Testing chat: Add resource form: Text') },
            {
              value: AgentResourceELMA365AppSettingsTypeEnum.Elma365App,
              label: t('Agent: Testing chat: Add resource form: ELMA365 App'),
            },
            {
              value: AgentResourceELMA365SessionSettingsTypeEnum.Elma365Session,
              label: t('Agent: Testing chat: Add resource form: ELMA365 Session'),
            },
            {
              value: AgentResourceELMA365FileSettingsTypeEnum.Elma365File,
              label: t('Agent: Testing chat: Add resource form: ELMA365 File'),
            },
          ]}
          value={resource.settings.type}
          onChange={onSettingsTypeChange}
        />
      </div>
      <div className={FORM_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Testing chat: Add resource form: Name: Title')}
        </IbTypography.Paragraph>
        <IbInput value={resource.name} onChange={onNameChange} />
      </div>
      {renderSettings()}
      <div className={FORM_FOOTER_CLASS_NAME}>
        <IbButton type="fill" onClick={onAddButtonClick}>
          {t('Agent: Testing chat: Add resource form: Add')}
        </IbButton>
      </div>
    </div>
  );
};

export default AddResourceForm;
